/*
 * Related products on a blog article (sections/product-recemendation.liquid).
 *
 * The article's custom.jackson_also_suggest_products metafield holds up to three hand
 * picked products. They show first (the section renders them marked
 * data-seed), followed by what Shopify's own recommendations API says goes
 * with each of them.
 *
 * Liquid only receives the `recommendations` object on Shopify's endpoint, so
 * the work happens here: one request per seed against
 * /recommendations/products?section_id=…&product_id=…, which renders this same
 * section in its fragment mode and hands back ready made slides. No card
 * markup is built in JavaScript.
 *
 * The answers are interleaved so every seed contributes and deduplicated
 * against the seeds, which stay at the front. Out of stock products never arrive, because
 * the section filters them server side.
 *
 * Nothing happens at all when a request fails or nothing survives the merge:
 * the fallback collection the section already rendered simply stays, which is
 * also what a visitor without JavaScript sees.
 *
 * Three things keep the cost down. The section sits below the whole article,
 * so nothing is requested until it is close to the viewport; each seed is
 * asked only for its share of the carousel rather than for a full one, which
 * is the difference between rendering eight cards server side and twenty four
 * to show eight; and the merged result is kept in sessionStorage, so going
 * back to an article costs nothing at all.
 */
(() => {
  if (window.jgBlogRelatedLoaded) return;
  window.jgBlogRelatedLoaded = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CACHE_KEY = 'jg:blog-related';
  const CACHE_MAX = 5;

  // sessionStorage, so it lasts as long as the tab and never goes stale across
  // visits. A handful of articles are kept, oldest dropped first, and the
  // theme editor skips it so a settings change is visible straight away.
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const readCache = (id) => {
    if (designMode) return null;
    try {
      const all = JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {};
      return all[id] ? all[id].html : null;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (id, html) => {
    if (designMode) return;
    try {
      const all = JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {};
      all[id] = { html, at: Date.now() };

      const ids = Object.keys(all);
      if (ids.length > CACHE_MAX) {
        ids
          .sort((a, b) => all[a].at - all[b].at)
          .slice(0, ids.length - CACHE_MAX)
          .forEach((old) => delete all[old]);
      }

      sessionStorage.setItem(CACHE_KEY, JSON.stringify(all));
    } catch (error) {
      // Private mode or a full quota: the carousel still works, it just
      // fetches again next time.
    }
  };

  const slidesFor = async (seedId, sectionId, limit) => {
    const url = `${ROOT}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(
      seedId
    )}&limit=${limit}&intent=related`;

    try {
      const response = await fetch(url);
      if (!response.ok) return [];
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      return [...parsed.querySelectorAll('.embla__slide[data-product-id]')];
    } catch (error) {
      return [];
    }
  };

  // Round robin, so three seeds each get a say instead of the first one
  // filling the carousel on its own.
  const merge = (lists, exclude, limit) => {
    const seen = new Set(exclude);
    const picked = [];
    const longest = lists.reduce((max, list) => Math.max(max, list.length), 0);

    for (let i = 0; i < longest && picked.length < limit; i += 1) {
      for (const list of lists) {
        if (picked.length >= limit) break;
        const slide = list[i];
        if (!slide) continue;

        const id = slide.dataset.productId;
        if (!id || seen.has(id)) continue;

        seen.add(id);
        picked.push(slide);
      }
    }

    return picked;
  };

  // The carousel node is replaced rather than refilled: Embla holds a
  // reference to the old one, and a fresh node has no "already initialised"
  // flag, so the section's own script sets it up again untouched.
  const swap = (root, html) => {
    const current = root.querySelector('[data-blog-related-carousel]');
    if (!current) return;

    const replacement = current.cloneNode(false);
    replacement.removeAttribute('data-embla-initialized');

    const viewport = document.createElement('div');
    viewport.className = 'embla__viewport';

    const track = document.createElement('div');
    track.className = 'embla__container';
    track.setAttribute('data-blog-related-track', '');
    track.innerHTML = html;

    viewport.appendChild(track);
    replacement.appendChild(viewport);

    const nav = current.querySelector('.hp-picks__nav');
    if (nav) replacement.appendChild(nav.cloneNode(true));

    current.replaceWith(replacement);

    // What the theme editor fires after swapping markup in; the carousel
    // script listens for it and skips anything already running.
    document.dispatchEvent(new Event('shopify:section:load'));
  };

  const run = async (root) => {
    const seedIds = (root.dataset.seedIds || '').split(',').filter(Boolean);
    if (!seedIds.length) return;

    const sectionId = root.dataset.sectionId;
    const limit = parseInt(root.dataset.limit, 10) || 8;

    const cacheId = `${sectionId}|${limit}|${seedIds.join(',')}`;
    const cached = readCache(cacheId);
    if (cached) {
      swap(root, cached);
      return;
    }

    // Each seed only owes its share of the carousel, plus a couple spare to
    // cover duplicates and the seeds dropping out of their own results.
    // Shopify caps the endpoint at ten either way.
    const perSeed = Math.min(Math.ceil(limit / seedIds.length) + 2, 10);

    const lists = await Promise.all(seedIds.map((id) => slidesFor(id, sectionId, perSeed)));
    const seedSlides = [...root.querySelectorAll('.embla__slide[data-seed]')];
    const recommended = merge(lists, seedIds, Math.max(limit - seedSlides.length, 0));
    if (!recommended.length) return;
    const slides = [...seedSlides, ...recommended];

    const html = slides.map((slide) => slide.outerHTML).join('');
    writeCache(cacheId, html);
    swap(root, html);
  };

  // The section is below the article, so a reader who never scrolls that far
  // costs nothing. The margin starts the requests early enough that the swap
  // has usually happened before it comes into view.
  const watch = (root) => {
    if (!('IntersectionObserver' in window)) {
      run(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        run(root);
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(root);
  };

  document.querySelectorAll('[data-blog-related]').forEach(watch);
})();
